// Báo cáo theo tháng (GĐ10, §4.7) — mọi số tiền đọc từ dòng tiền (MoneyLine) ở máy chủ.
//  · Đại lý: GET /reports/agency-monthly (chênh lệch hưởng, hoa hồng đã trả CTV, ròng, thực nhận
//    đơn tự bán) + GET /reports/ctv-monthly (từng CTV của mình).
//  · CTV:    GET /reports/ctv-monthly (của chính mình).
// Tháng tính theo kết cục của đơn (giao thành công / bắt đầu hoàn / thất lạc); hoa hồng theo tháng
// hoa hồng. "Xuất Excel" làm ở trình duyệt (thư viện xlsx).

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, Coins, Download, HandCoins, ListChecks, PackageCheck, Scale, Store, Wallet } from 'lucide-react'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import { get } from '@/lib/api'
import { money } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { KpiTile } from '@/components/ui/kpi-tile'
import { LoadError } from '@/components/ui/data-notice'
import { Section } from '@/components/ui/section'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { MonthStepper } from '@/components/commissions/parts'
import { MONTH_STATUS_LABEL, currentMonthVN, monthLabel } from '@/components/commissions/types'

export interface CtvMonthRow {
  ctvShopId: string
  name: string
  parentName: string | null
  ownOrders: { delivered: number; returned: number }
  networkOrders: { delivered: number; returned: number }
  countedOrders: number
  tierRate: number | null
  commission: number
  payer: string
  monthStatus: 'provisional' | 'closed' | null
  paid: number
  pending: number
}

export interface AgencyMonthRow {
  agencyShopId: string
  name: string
  margin: number
  commissionPaid: number
  net: number
  ownSales: number
  ownOrders: number
  networkOrders: number
}

const n = (x: number) => x.toLocaleString('vi-VN')
const statusText = (s: CtvMonthRow['monthStatus']) => (s ? (MONTH_STATUS_LABEL[s] ?? s) : 'Chưa có chiết khấu')

const OUTCOME_NOTE = 'Tính theo tháng đơn có kết quả: giao thành công, bắt đầu hoàn hoặc thất lạc. Chiết khấu tính theo tháng chiết khấu của đơn.'

// ─── Xuất Excel ──────────────────────────────────────────────────

type Cell = string | number | null

async function exportXlsx(fileName: string, sheets: { name: string; rows: Cell[][] }[]) {
  const wb = XLSX.utils.book_new()
  for (const sh of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(sh.rows)
    const cols = Math.max(...sh.rows.map((r) => r.length))
    ws['!cols'] = Array.from({ length: cols }, (_, i) => ({ wch: Math.min(40, Math.max(10, ...sh.rows.map((r) => String(r[i] ?? '').length + 2))) }))
    XLSX.utils.book_append_sheet(wb, ws, sh.name.slice(0, 31))
  }
  XLSX.writeFile(wb, fileName)
}

const CTV_HEAD: Cell[] = [
  'CTV',
  'Cấp trên',
  'Đơn tự bán giao thành công',
  'Đơn tự bán hoàn',
  'Đơn của shop giao thành công',
  'Đơn của shop hoàn',
  'Đơn tính chiết khấu',
  'Mức đạt (đ/đơn)',
  'Chiết khấu (đ)',
  'Người trả',
  'Trạng thái tháng',
  'Đã chi (đ)',
  'Chờ chi (đ)',
]

const ctvCells = (r: CtvMonthRow): Cell[] => [
  r.name,
  r.parentName ?? '',
  r.ownOrders.delivered,
  r.ownOrders.returned,
  r.networkOrders.delivered,
  r.networkOrders.returned,
  r.countedOrders,
  r.tierRate,
  r.commission,
  r.payer,
  statusText(r.monthStatus),
  r.paid,
  r.pending,
]

function ExportButton({ disabled, onExport }: { disabled: boolean; onExport: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant="outline"
      className="gap-1.5"
      disabled={disabled || busy}
      onClick={async () => {
        setBusy(true)
        try {
          await onExport()
        } catch (e) {
          toast.error(`Không xuất được file: ${(e as Error).message}`)
        } finally {
          setBusy(false)
        }
      }}
    >
      <Download className="size-4" /> {busy ? 'Đang xuất…' : 'Xuất Excel'}
    </Button>
  )
}

// ─── Đại lý ──────────────────────────────────────────────────────

export function AgencyMonthlyReport() {
  const nowMonth = currentMonthVN()
  const [month, setMonth] = useState(nowMonth)
  const agencyQ = useQuery({
    queryKey: ['reports', 'agency-monthly', month],
    queryFn: () => get<{ month: string; row: AgencyMonthRow | null }>(`/reports/agency-monthly?month=${month}`),
  })
  const ctvQ = useQuery({
    queryKey: ['reports', 'ctv-monthly', month],
    queryFn: () => get<{ month: string; rows: CtvMonthRow[] }>(`/reports/ctv-monthly?month=${month}`),
  })
  const a = agencyQ.data?.row ?? null
  const rows = ctvQ.data?.rows ?? []
  const loadingA = agencyQ.isLoading

  async function onExport() {
    const sheets: { name: string; rows: Cell[][] }[] = [
      {
        name: 'Đại lý',
        rows: [
          [`Báo cáo Đại lý tháng ${monthLabel(month)}`],
          [],
          ['Chỉ tiêu', 'Giá trị'],
          ['Chênh lệch hưởng (đ)', a?.margin ?? 0],
          ['Chiết khấu đã trả CTV (đ)', a?.commissionPaid ?? 0],
          ['Ròng (đ)', a?.net ?? 0],
          ['Thực nhận đơn tự bán (đ)', a?.ownSales ?? 0],
          ['Số đơn tự bán', a?.ownOrders ?? 0],
          ['Số đơn của cấp dưới', a?.networkOrders ?? 0],
          [],
          [OUTCOME_NOTE],
        ],
      },
      { name: 'CTV theo tháng', rows: [CTV_HEAD, ...rows.map(ctvCells)] },
    ]
    await exportXlsx(`Bao_cao_thang_${month}.xlsx`, sheets)
  }

  const columns: DataTableColumn<CtvMonthRow>[] = [
    { key: 'name', header: 'CTV', priority: 'primary', cell: (r) => <span className="font-medium text-ink">{r.name}</span> },
    {
      key: 'own',
      header: 'Đơn tự bán',
      align: 'right',
      cell: (r) => <OrdersPair delivered={r.ownOrders.delivered} returned={r.ownOrders.returned} />,
    },
    {
      key: 'network',
      header: 'Đơn của shop',
      align: 'right',
      cell: (r) => <OrdersPair delivered={r.networkOrders.delivered} returned={r.networkOrders.returned} />,
    },
    { key: 'counted', header: 'Đơn tính chiết khấu', align: 'right', cell: (r) => <span className="tabular-nums text-ink">{n(r.countedOrders)}</span> },
    {
      key: 'rate',
      header: 'Mức đạt',
      align: 'right',
      cell: (r) => <span className="whitespace-nowrap tabular-nums text-inkSoft">{r.tierRate == null ? '—' : `${n(r.tierRate)}đ/đơn`}</span>,
    },
    { key: 'commission', header: 'Chiết khấu', align: 'right', cell: (r) => <span className="whitespace-nowrap font-mono font-semibold tabular-nums text-ink">{money(r.commission)}</span> },
    { key: 'payer', header: 'Người trả', cell: (r) => <span className="text-inkSoft">{r.payer}</span> },
    { key: 'status', header: 'Trạng thái', cell: (r) => <MonthStatusBadge status={r.monthStatus} /> },
    {
      key: 'paid',
      header: 'Đã chi / chờ chi',
      align: 'right',
      cell: (r) => (
        <span className="block whitespace-nowrap font-mono text-xs tabular-nums">
          <span className="block text-ok">{money(r.paid)}</span>
          <span className="block text-inkSoft">{money(r.pending)}</span>
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2>Theo tháng</h2>
        <div className="flex flex-wrap items-center gap-2">
          <MonthStepper value={month} onChange={setMonth} max={nowMonth} />
          <ExportButton disabled={agencyQ.isLoading || ctvQ.isLoading || agencyQ.isError || ctvQ.isError} onExport={onExport} />
        </div>
      </div>

      {agencyQ.isError ? (
        <Section>
          <LoadError onRetry={() => agencyQ.refetch()}>Không tải được báo cáo Đại lý tháng {monthLabel(month)}.</LoadError>
        </Section>
      ) : (
        <div className="grid grid-cols-2 gap-3 min-[1200px]:grid-cols-4">
          <KpiTile tone="violet" icon={Scale} label="Chênh lệch hưởng" value={money(a?.margin ?? 0)} hint={`${n(a?.networkOrders ?? 0)} đơn của cấp dưới`} loading={loadingA} />
          <KpiTile tone="orange" icon={HandCoins} label="Chiết khấu đã trả CTV" value={money(a?.commissionPaid ?? 0)} loading={loadingA} />
          <KpiTile tone="green" icon={Coins} label="Ròng" value={money(a?.net ?? 0)} loading={loadingA} />
          <KpiTile tone="blue" icon={Wallet} label="Thực nhận đơn tự bán" value={money(a?.ownSales ?? 0)} hint={`${n(a?.ownOrders ?? 0)} đơn`} loading={loadingA} />
        </div>
      )}

      <Section title={`CTV của tôi · tháng ${monthLabel(month)}`}>
        {ctvQ.isError ? (
          <LoadError onRetry={() => ctvQ.refetch()}>Không tải được báo cáo CTV.</LoadError>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(r) => r.ctvShopId}
            loading={ctvQ.isLoading}
            emptyLabel="Chưa có đơn"
          />
        )}
      </Section>
    </div>
  )
}

// ─── CTV ─────────────────────────────────────────────────────────

export function CtvMonthlyReport() {
  const nowMonth = currentMonthVN()
  const [month, setMonth] = useState(nowMonth)
  const q = useQuery({
    queryKey: ['reports', 'ctv-monthly', month],
    queryFn: () => get<{ month: string; rows: CtvMonthRow[] }>(`/reports/ctv-monthly?month=${month}`),
  })
  const r = q.data?.rows[0] ?? null
  const loading = q.isLoading

  async function onExport() {
    await exportXlsx(`Bao_cao_CTV_${month}.xlsx`, [{ name: 'CTV theo tháng', rows: [CTV_HEAD, ...(q.data?.rows ?? []).map(ctvCells), [], [OUTCOME_NOTE]] }])
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2>Theo tháng</h2>
        <div className="flex flex-wrap items-center gap-2">
          <MonthStepper value={month} onChange={setMonth} max={nowMonth} />
          <ExportButton disabled={q.isLoading || q.isError || !r} onExport={onExport} />
        </div>
      </div>

      {q.isError ? (
        <Section>
          <LoadError onRetry={() => q.refetch()}>Không tải được báo cáo tháng {monthLabel(month)}.</LoadError>
        </Section>
      ) : !loading && !r ? (
        <Section>
          <p className="px-4 py-10 text-center text-sm text-muted">Chưa có đơn</p>
        </Section>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 min-[1200px]:grid-cols-4">
            <KpiTile
              tone="blue"
              icon={PackageCheck}
              label="Đơn tự bán"
              value={`${n(r?.ownOrders.delivered ?? 0)} giao`}
              hint={`${n(r?.ownOrders.returned ?? 0)} đơn hoàn`}
              loading={loading}
            />
            <KpiTile
              tone="green"
              icon={Store}
              label="Đơn của shop"
              value={`${n(r?.networkOrders.delivered ?? 0)} giao`}
              hint={`${n(r?.networkOrders.returned ?? 0)} đơn hoàn`}
              loading={loading}
            />
            <KpiTile
              tone="violet"
              icon={ListChecks}
              label="Đơn tính chiết khấu"
              value={n(r?.countedOrders ?? 0)}
              hint={r?.tierRate != null ? `Mức đạt ${n(r.tierRate)}đ/đơn` : undefined}
              loading={loading}
            />
            <KpiTile
              tone="orange"
              icon={BarChart3}
              label="Chiết khấu"
              value={money(r?.commission ?? 0)}
              hint={r ? [r.monthStatus ? statusText(r.monthStatus) : null, r.payer === '—' ? null : `${r.payer} trả`].filter(Boolean).join(' · ') || undefined : undefined}
              loading={loading}
            >
              {r && (
                <ul className="space-y-0.5 text-xs">
                  <li className="flex justify-between gap-2">
                    <span>Đã chi</span>
                    <b className="font-semibold tabular-nums">{money(r.paid)}</b>
                  </li>
                  <li className="flex justify-between gap-2">
                    <span>Chờ chi</span>
                    <b className="font-semibold tabular-nums">{money(r.pending)}</b>
                  </li>
                </ul>
              )}
            </KpiTile>
          </div>
        </>
      )}
    </div>
  )
}

function OrdersPair({ delivered, returned }: { delivered: number; returned: number }) {
  return (
    <span className="block whitespace-nowrap tabular-nums">
      <span className="text-ink">{n(delivered)} giao</span>
      <span className={cn('ml-1.5 text-xs', returned > 0 ? 'text-warn' : 'text-muted')}>· {n(returned)} hoàn</span>
    </span>
  )
}

function MonthStatusBadge({ status }: { status: CtvMonthRow['monthStatus'] }) {
  return (
    <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', status === 'closed' ? 'bg-ok/12 text-ok' : status ? 'bg-info/12 text-info' : 'bg-paperSoft text-inkSoft')}>
      {statusText(status)}
    </span>
  )
}
